import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Zap, 
  Home, 
  Search, 
  Users, 
  CreditCard, 
  Activity, 
  Settings, 
  LogOut, 
  Server,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  ArrowRightLeft,
  Clock,
  FileSpreadsheet
} from 'lucide-react';
import { toast } from 'react-toastify';

const Sidebar = ({ user, onLogout, isCollapsed, setIsCollapsed }) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Configuração dos itens de navegação baseada no role
  const getNavigationItems = () => {
    // Para usuários com role 'user', mostrar apenas Consultas
    if (user?.role === 'user') {
      return [
        { id: 'search', label: 'Consultas', icon: Search, path: '/search', roles: ['admin', 'manager', 'user'] }
      ];
    }
    
    // Para outros roles (admin, manager)
    const items = [
      { id: 'search', label: 'Consultas', icon: Search, path: '/search', roles: ['admin', 'manager', 'user'] },
      { id: 'cobranca', label: 'Nova Cobrança', icon: CreditCard, path: '/cobranca', roles: ['admin', 'manager', 'user'] },
      { id: 'timeline', label: 'Timeline Bitrix', icon: Clock, path: '/timeline', roles: ['admin', 'manager', 'user'] },
      { id: 'migration', label: 'Migração', icon: ArrowRightLeft, path: '/migration', roles: ['admin', 'manager'] },
      { id: 'csv-import', label: 'Importar CSV', icon: FileSpreadsheet, path: '/csv-import', roles: ['admin', 'manager'] },
      { id: 'apis', label: 'Status APIs', icon: Activity, path: '/apis', roles: ['admin', 'manager', 'user'] }
    ];

    // Adicionar itens específicos por role
    if (user?.role === 'admin') {
      items.unshift({ id: 'dashboard', label: 'Dashboard', icon: Home, path: '/dashboard', roles: ['admin'] });
      items.push({ id: 'users', label: 'Usuários', icon: Users, path: '/users', roles: ['admin', 'manager'] });
      items.push({ id: 'reprocess', label: 'Reprocessar JSON', icon: Server, path: '/reprocess', roles: ['admin'] });
      items.push({ id: 'settings', label: 'Configurações', icon: Settings, path: '/settings', roles: ['admin'] });
    } else if (user?.role === 'manager') {
      items.push({ id: 'users', label: 'Usuários', icon: Users, path: '/users', roles: ['admin', 'manager'] });
    }

    return items;
  };

  const navigationItems = getNavigationItems();

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileOpen(false);
  };

  const handleLogout = async () => {
    try {
      await onLogout();
      toast.success('Logout realizado com sucesso!');
    } catch (error) {
      console.error('Erro no logout:', error);
      toast.error('Erro ao fazer logout');
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return 'Admin';
      case 'manager': return 'Gerente';
      case 'user': return 'Usuário';
      default: return 'Usuário';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-500';
      case 'manager': return 'bg-blue-500';
      case 'user': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  // Fechar mobile sidebar quando redimensionar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {/* Mobile Overlay */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setIsMobileOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Mobile Toggle Button */}
      <motion.button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="fixed top-4 left-4 z-50 lg:hidden p-3 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {isMobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </motion.button>

      {/* Sidebar */}
      <motion.div
        initial={{ x: -300 }}
        animate={{ 
          x: 0,
          width: isCollapsed ? 80 : 280 
        }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className={`fixed left-0 top-0 h-full bg-white shadow-2xl border-r border-gray-200 z-40 ${
          isMobileOpen ? 'block' : 'hidden lg:block'
        }`}
      >
        {/* Header da Sidebar */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {!isCollapsed && (
              <motion.div 
                className="flex items-center space-x-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-gray-900">Painel Bitrix</h1>
                  <p className="text-xs text-gray-500">Sistema de Consulta</p>
                </div>
              </motion.div>
            )}
            
            {/* Toggle Button - Desktop */}
            <motion.button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="hidden lg:flex p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </motion.button>
          </div>
        </div>

        {/* Navegação */}
        <nav className="flex-1 py-6">
          <div className="space-y-2 px-4">
            {navigationItems.map((item, index) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <motion.button
                  key={item.id}
                  onClick={() => handleNavigation(item.path)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-300 group relative ${
                    isActive 
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg' 
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  whileHover={{ scale: 1.02, x: 5 }}
                  whileTap={{ scale: 0.98 }}
                  title={isCollapsed ? item.label : ''}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-600 group-hover:text-gray-900'}`} />
                  
                  {!isCollapsed && (
                    <motion.span 
                      className="font-medium"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      {item.label}
                    </motion.span>
                  )}
                  
                  {isActive && (
                    <motion.div
                      layoutId="activeIndicator"
                      className="absolute right-2 w-2 h-2 bg-white rounded-full"
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  )}
                </motion.button>
              );
            })}
          </div>
        </nav>

        {/* User Info & Logout */}
        <div className="border-t border-gray-200 p-4">
          {!isCollapsed ? (
            <motion.div 
              className="space-y-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              {/* User Info */}
              <div className="flex items-center space-x-3 p-3 rounded-xl bg-gray-50">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-sm">
                    {(user?.full_name || user?.username || 'U').charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user?.full_name || user?.username}
                  </p>
                  <div className="flex items-center space-x-2">
                    <span className={`w-2 h-2 rounded-full ${getRoleColor(user?.role)}`}></span>
                    <span className="text-xs text-gray-500">{getRoleLabel(user?.role)}</span>
                  </div>
                </div>
              </div>

              {/* Logout Button */}
              <motion.button
                onClick={handleLogout}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-all duration-300"
                whileHover={{ scale: 1.02, x: 5 }}
                whileTap={{ scale: 0.98 }}
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Sair</span>
              </motion.button>
            </motion.div>
          ) : (
            <div className="space-y-2">
              {/* User Avatar Collapsed */}
              <div className="flex justify-center">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-sm">
                    {(user?.full_name || user?.username || 'U').charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Logout Collapsed */}
              <motion.button
                onClick={handleLogout}
                className="w-full flex justify-center p-3 rounded-xl text-red-600 hover:bg-red-50 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title="Sair"
              >
                <LogOut className="w-5 h-5" />
              </motion.button>
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
};

export default Sidebar;
